/**
 * MeSH (Medical Subject Headings) from the National Library of Medicine's public services.
 *
 * Everything here is read from `id.nlm.nih.gov/mesh`, which answers browsers directly (it
 * sends `Access-Control-Allow-Origin: *`) and needs no key:
 *
 * - `lookup/descriptor` and `lookup/term` — fast label searches (headings and entry terms);
 * - `lookup/details?descriptor=` — a heading's entry terms and allowable subheadings;
 * - `D…​.json` and `M…​.json` — a heading's and concept's linked-data record;
 * - `sparql` — used only for small, fully anchored queries (a heading's neighbours, or
 *   which heading owns an entry term). Free-text SPARQL searches take 15–20 seconds, so
 *   searching never uses them.
 *
 * The PubMed syntax follows the PubMed User Guide: `"Term"[Mesh]` searches a heading and,
 * by default, every heading below it in the tree ("explode"); `[Mesh:NoExp]` stops at the
 * heading itself; `[Majr]` restricts to articles where it is a major topic; `[tiab]`
 * searches title and abstract words.
 */

import { fetchJson } from "@/lib/tool-kit/reference-tools/network";

const BASE = "https://id.nlm.nih.gov/mesh";

const RE_DESCRIPTOR_ID = /^D\d{6,9}$/;
const RE_TERM_ID = /^T\d{6,9}$/;
const RE_ID_TAIL = /([A-Z]\d+)$/;
const RE_QUOTES = /["“”]/g;
const RE_WHITESPACE = /\s+/g;
const RE_DATE_YEAR = /^(\d{4})/;

export const LOOKUP_LIMIT = 50;
/** Entry-term matches resolved to headings in one SPARQL call. */
export const VIA_TERM_LIMIT = 8;
export const HEADING_LIMIT = 15;

export function isDescriptorId(id: string): boolean {
  return RE_DESCRIPTOR_ID.test(id);
}

export function descriptorLookupUrl(text: string): string {
  return `${BASE}/lookup/descriptor?label=${encodeURIComponent(text.trim())}&match=contains&limit=${LOOKUP_LIMIT}`;
}

export function termLookupUrl(text: string): string {
  return `${BASE}/lookup/term?label=${encodeURIComponent(text.trim())}&match=contains&limit=${LOOKUP_LIMIT}`;
}

export function detailsUrl(id: string): string {
  return `${BASE}/lookup/details?descriptor=${id}`;
}

export function recordUrl(id: string): string {
  return `${BASE}/${id}.json`;
}

export function sparqlUrl(query: string): string {
  return `${BASE}/sparql?format=JSON&query=${encodeURIComponent(query)}`;
}

const PREFIXES = `PREFIX meshv: <http://id.nlm.nih.gov/mesh/vocab#>
PREFIX mesh: <http://id.nlm.nih.gov/mesh/>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
`;

/** Which heading owns each entry term. IDs are validated, so nothing user-typed is interpolated. */
export function termOwnersQuery(termIds: readonly string[]): string {
  const values = termIds
    .filter((id) => RE_TERM_ID.test(id))
    .map((id) => `mesh:${id}`)
    .join(" ");
  return `${PREFIXES}SELECT ?t ?d ?label WHERE {
  VALUES ?t { ${values} }
  ?c (meshv:preferredTerm|meshv:term) ?t .
  ?d (meshv:preferredConcept|meshv:concept) ?c ; rdfs:label ?label .
}`;
}

/** The headings directly above and below one heading in the tree. */
export function neighboursQuery(id: string): string {
  if (!RE_DESCRIPTOR_ID.test(id)) {
    throw new Error("Not a MeSH descriptor ID");
  }
  return `${PREFIXES}SELECT ?kind ?d ?label WHERE {
  { ?d meshv:broaderDescriptor mesh:${id} ; rdfs:label ?label . BIND("narrower" AS ?kind) }
  UNION { mesh:${id} meshv:broaderDescriptor ?d . ?d rdfs:label ?label . BIND("broader" AS ?kind) }
}`;
}

/* --------------------------------------------------------------- parsing */

export interface MeshHit {
  /** "D009203" for a heading, "T000866941" for a term. */
  id: string;
  label: string;
}

export interface MeshCandidate extends MeshHit {
  /** The entry term that matched, when the heading was found through one. */
  via?: string;
}

function idOf(iri: unknown): string {
  const match = RE_ID_TAIL.exec(typeof iri === "string" ? iri : "");
  return match ? match[1] : "";
}

function text(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }
  if (Array.isArray(value)) {
    return text(value[0]);
  }
  if (value && typeof value === "object") {
    return text((value as Record<string, unknown>)["@value"]);
  }
  return "";
}

function list(value: unknown): unknown[] {
  if (value === undefined || value === null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

/** A lookup response as hits. */
export function parseLookup(body: unknown): MeshHit[] {
  if (!Array.isArray(body)) {
    return [];
  }
  return body.flatMap((entry) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    const id = idOf(row.resource);
    const label = text(row.label);
    return id && label ? [{ id, label }] : [];
  });
}

/** Exact match first, then labels starting with the text, then words starting with it. */
export function rankHits(hits: readonly MeshHit[], query: string): MeshHit[] {
  const needle = query.trim().toLowerCase();
  const score = (hit: MeshHit): number => {
    const label = hit.label.toLowerCase();
    if (label === needle) {
      return 0;
    }
    if (label.startsWith(needle)) {
      return 1;
    }
    if (label.includes(` ${needle}`)) {
      return 2;
    }
    return 3;
  };
  return [...hits].sort(
    (a, b) =>
      score(a) - score(b) ||
      a.label.length - b.label.length ||
      a.label.localeCompare(b.label)
  );
}

/** SPARQL bindings as plain string maps. */
function bindings(body: unknown): Record<string, string>[] {
  const rows = (body as { results?: { bindings?: unknown } } | null)?.results
    ?.bindings;
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row) =>
    Object.fromEntries(
      Object.entries((row ?? {}) as Record<string, { value?: unknown }>).map(
        ([key, cell]) => [key, text(cell?.value)]
      )
    )
  );
}

export function parseTermOwners(
  body: unknown,
  termLabels: ReadonlyMap<string, string>
): MeshCandidate[] {
  const out: MeshCandidate[] = [];
  const seen = new Set<string>();
  for (const row of bindings(body)) {
    const id = idOf(row.d);
    if (!(id && row.label) || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push({ id, label: row.label, via: termLabels.get(idOf(row.t)) });
  }
  return out;
}

/* -------------------------------------------------------------- details */

export interface MeshRelative {
  id: string;
  label: string;
}

export interface MeshDescriptor {
  id: string;
  label: string;
  /** "Topical descriptor", "Publication type"… as MeSH names its class. */
  kind: string;
  treeNumbers: string[];
  scopeNote: string;
  annotation: string;
  /** How the heading was indexed before it took its current form. */
  previousIndexing: string;
  /** Year the heading entered MeSH, or "". */
  yearIntroduced: string;
  /** Non-preferred terms that lead to the heading. */
  entryTerms: string[];
  subheadings: string[];
  broader: MeshRelative[];
  narrower: MeshRelative[];
  seeAlso: MeshRelative[];
}

const KINDS: Record<string, string> = {
  TopicalDescriptor: "topical",
  GeographicalDescriptor: "geographical",
  PublicationType: "publication-type",
  CheckTag: "check-tag",
};

export function parseNeighbours(body: unknown): {
  broader: MeshRelative[];
  narrower: MeshRelative[];
} {
  const out = { broader: [] as MeshRelative[], narrower: [] as MeshRelative[] };
  for (const row of bindings(body)) {
    const id = idOf(row.d);
    if (!(id && row.label)) {
      continue;
    }
    const target = row.kind === "broader" ? out.broader : out.narrower;
    target.push({ id, label: row.label });
  }
  out.narrower.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}

function relatives(value: unknown): MeshRelative[] {
  return list(value).flatMap((entry) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    const id = idOf(row.resource);
    const label = text(row.label);
    return id && label ? [{ id, label }] : [];
  });
}

/** Everything the page shows, from the three fast responses plus the neighbours. */
export function parseDescriptor(
  id: string,
  record: unknown,
  details: unknown,
  concept: unknown,
  neighbours: { broader: MeshRelative[]; narrower: MeshRelative[] }
): MeshDescriptor | null {
  const rec = (record ?? {}) as Record<string, unknown>;
  const det = (details ?? {}) as Record<string, unknown>;
  const label = text(rec.label);
  if (label === "") {
    return null;
  }
  const preferred = idOf(rec.preferredTerm);
  const terms = list(det.terms).flatMap((entry) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    const name = text(row.label);
    const isPreferred =
      row.preferred === true || idOf(row.resource) === preferred;
    return name && !isPreferred ? [name] : [];
  });
  const kind = KINDS[String(rec["@type"]).split("#").at(-1) ?? ""] ?? "topical";
  return {
    id,
    label,
    kind,
    treeNumbers: list(rec.treeNumber)
      .map(idOfTree)
      .filter((n) => n !== ""),
    scopeNote: text((concept as Record<string, unknown> | null)?.scopeNote),
    annotation: text(rec.annotation),
    previousIndexing: text(rec.previousIndexing),
    yearIntroduced: RE_DATE_YEAR.exec(text(rec.dateIntroduced))?.[1] ?? "",
    entryTerms: [...new Set(terms)],
    subheadings: relatives(det.qualifiers).map((q) => q.label),
    broader: neighbours.broader,
    narrower: neighbours.narrower,
    seeAlso: relatives(det.seealso),
  };
}

function idOfTree(iri: unknown): string {
  return typeof iri === "string" ? (iri.split("/").at(-1) ?? "") : "";
}

/** The MeSH tree category a tree number sits in, by its first letter. */
export function treeCategory(treeNumber: string): string {
  return treeNumber.charAt(0).toUpperCase();
}

/** Depth of a tree number: C14 is level 1, C14.280 is level 2. */
export function treeLevel(treeNumber: string): number {
  return treeNumber.split(".").length;
}

/* ------------------------------------------------------------ PubMed string */

export interface PubmedOptions {
  /** Include narrower headings (PubMed's default). */
  explode: boolean;
  /** Only articles where the heading is a major topic. */
  majorTopic: boolean;
  /** Add the heading and its entry terms as title/abstract words. */
  freeText: boolean;
  /** Free-text words the reader switched off, as `skipKey` strings. */
  skip?: ReadonlySet<string>;
}

/** Identifies one free-text word of one heading, for switching it off. */
export function skipKey(descriptorId: string, term: string): string {
  return `${descriptorId}|${term.toLowerCase()}`;
}

/** At most this many entry terms per heading go into the free-text part. */
export const FREE_TEXT_TERMS = 8;

function quoted(value: string): string {
  return `"${value.replace(RE_QUOTES, "").replace(RE_WHITESPACE, " ").trim()}"`;
}

export function meshClause(label: string, options: PubmedOptions): string {
  const field = options.majorTopic ? "Majr" : "Mesh";
  const suffix = options.explode ? "" : ":NoExp";
  return `${quoted(label)}[${field}${suffix}]`;
}

/** A heading's name and entry terms, once each, as offered for free-text searching. */
export function freeTextCandidates(descriptor: MeshDescriptor): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const term of [descriptor.label, ...descriptor.entryTerms]) {
    const key = term.toLowerCase();
    if (!seen.has(key) && out.length <= FREE_TEXT_TERMS) {
      seen.add(key);
      out.push(term);
    }
  }
  return out;
}

/** The free-text words for a heading, each as `"…"[tiab]`, less any switched off. */
export function freeTextTerms(
  descriptor: MeshDescriptor,
  skip: ReadonlySet<string> = new Set()
): string[] {
  return freeTextCandidates(descriptor)
    .filter((term) => !skip.has(skipKey(descriptor.id, term)))
    .map((term) => `${quoted(term)}[tiab]`);
}

/** One OR-block for a set of headings: the clauses joined and wrapped in parentheses. */
export function buildPubmedString(
  descriptors: readonly MeshDescriptor[],
  options: PubmedOptions
): string {
  const parts: string[] = [];
  for (const descriptor of descriptors) {
    parts.push(meshClause(descriptor.label, options));
    if (options.freeText) {
      parts.push(...freeTextTerms(descriptor, options.skip));
    }
  }
  const unique = [...new Set(parts)];
  if (unique.length === 0) {
    return "";
  }
  return unique.length === 1 ? unique[0] : `(${unique.join(" OR ")})`;
}

export function pubmedSearchUrl(query: string): string {
  return `https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(query)}`;
}

/* --------------------------------------------------------------- running */

export type MeshProblem = "tooShort" | "rateLimited" | "failed";

export interface SearchOutcome {
  headings: MeshCandidate[];
  viaTerms: MeshCandidate[];
  /** True when entry-term matches existed but could not be resolved to headings. */
  termsUnresolved: boolean;
}

export type SearchResult =
  | { ok: true; outcome: SearchOutcome }
  | { ok: false; problem: MeshProblem };

function problemOf(reason: string): MeshProblem {
  return reason === "rate-limited" ? "rateLimited" : "failed";
}

async function resolveTerms(
  terms: readonly MeshHit[],
  signal: AbortSignal
): Promise<{ found: MeshCandidate[]; unresolved: boolean }> {
  if (terms.length === 0) {
    return { found: [], unresolved: false };
  }
  const response = await fetchJson<unknown>(
    sparqlUrl(termOwnersQuery(terms.map((term) => term.id))),
    { signal, retries: 0 }
  );
  if (!response.ok) {
    return { found: [], unresolved: true };
  }
  const labels = new Map(terms.map((term) => [term.id, term.label]));
  return { found: parseTermOwners(response.value, labels), unresolved: false };
}

/**
 * Search headings and entry terms for `text`. The two lookups run together; entry terms
 * that are not themselves headings are then traced to the heading they belong to.
 * Rethrows when `signal` aborts.
 */
export async function searchMesh(
  text: string,
  signal: AbortSignal
): Promise<SearchResult> {
  const query = text.trim();
  if (query.length < 2) {
    return { ok: false, problem: "tooShort" };
  }
  const [headingResponse, termResponse] = await Promise.all([
    fetchJson<unknown>(descriptorLookupUrl(query), { signal, retries: 1 }),
    fetchJson<unknown>(termLookupUrl(query), { signal, retries: 1 }),
  ]);
  if (!(headingResponse.ok || termResponse.ok)) {
    const reason = headingResponse.ok ? "network" : headingResponse.reason;
    return { ok: false, problem: problemOf(reason) };
  }
  const headings = rankHits(
    headingResponse.ok ? parseLookup(headingResponse.value) : [],
    query
  ).slice(0, HEADING_LIMIT);
  const known = new Set(headings.map((hit) => hit.label.toLowerCase()));
  const termHits = rankHits(
    termResponse.ok ? parseLookup(termResponse.value) : [],
    query
  )
    .filter((hit) => !known.has(hit.label.toLowerCase()))
    .slice(0, VIA_TERM_LIMIT);
  const { found, unresolved } = await resolveTerms(termHits, signal);
  const headingIds = new Set(headings.map((hit) => hit.id));
  return {
    ok: true,
    outcome: {
      headings,
      viaTerms: found.filter((hit) => !headingIds.has(hit.id)),
      termsUnresolved: unresolved,
    },
  };
}

export type DescriptorResult =
  | { ok: true; descriptor: MeshDescriptor }
  | { ok: false; problem: MeshProblem };

/** Load one heading's full record. Rethrows when `signal` aborts. */
export async function loadDescriptor(
  id: string,
  signal: AbortSignal
): Promise<DescriptorResult> {
  if (!isDescriptorId(id)) {
    return { ok: false, problem: "failed" };
  }
  const [record, details, neighbours] = await Promise.all([
    fetchJson<Record<string, unknown>>(recordUrl(id), { signal, retries: 1 }),
    fetchJson<unknown>(detailsUrl(id), { signal, retries: 1 }),
    fetchJson<unknown>(sparqlUrl(neighboursQuery(id)), { signal, retries: 0 }),
  ]);
  if (!record.ok) {
    return { ok: false, problem: problemOf(record.reason) };
  }
  const conceptId = idOf(record.value.preferredConcept);
  const concept = conceptId
    ? await fetchJson<unknown>(recordUrl(conceptId), { signal, retries: 0 })
    : null;
  const parsed = parseDescriptor(
    id,
    record.value,
    details.ok ? details.value : null,
    concept?.ok ? concept.value : null,
    neighbours.ok
      ? parseNeighbours(neighbours.value)
      : { broader: [], narrower: [] }
  );
  return parsed
    ? { ok: true, descriptor: parsed }
    : { ok: false, problem: "failed" };
}
